import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useTranslation } from '../../context/TranslationContext';
import { translateWithFallback } from '../../utils/translateWithFallback';
import type { TourStep } from './tours/types';

interface TourTooltipProps {
    step: TourStep;
    stepIndex: number;
    stepCount: number;
    style: React.CSSProperties;
    /** Soft note when the page is still pinch-zoomed (browsers often block forced unzoom). */
    zoomHint?: boolean;
    onNext: () => void;
    onSkip: () => void;
    onPrimaryAction?: () => void;
}

export type TourTooltipHandle = {
    getSize: () => { width: number; height: number };
};

const TourTooltip = forwardRef<TourTooltipHandle, TourTooltipProps>(function TourTooltip(
    { step, stepIndex, stepCount, style, zoomHint, onNext, onSkip, onPrimaryAction },
    ref
) {
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    const panelRef = useRef<HTMLDivElement>(null);
    const isLast = stepIndex >= stepCount - 1;

    useImperativeHandle(ref, () => ({
        getSize: () => {
            const el = panelRef.current;
            if (!el) return { width: 320, height: 300 };
            const r = el.getBoundingClientRect();
            return { width: r.width || 320, height: r.height || 300 };
        },
    }));

    useEffect(() => {
        panelRef.current?.focus();
    }, [step.id]);

    return (
        <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="product-tour-title"
            tabIndex={-1}
            className="absolute z-[10040] w-[min(calc(100vw-1.5rem),20rem)] rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_45%)] bg-[var(--bg-color)] p-4 shadow-xl outline-none"
            style={style}
        >
            <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-[color-mix(in_srgb,var(--text-color),transparent_45%)]">
                {tr('tour.common.step', 'Step {current} of {total}')
                    .replace('{current}', String(stepIndex + 1))
                    .replace('{total}', String(stepCount))}
            </p>
            {zoomHint && (
                <p className="mb-2 rounded-lg border border-[color-mix(in_srgb,var(--accent-color),transparent_40%)] bg-[color-mix(in_srgb,var(--accent-color),transparent_70%)] px-2.5 py-1.5 text-xs font-medium text-[var(--text-color)]">
                    {tr(
                        'tour.zoom.hint',
                        'For the clearest tour, pinch out to 100%'
                    )}
                </p>
            )}
            <h2 id="product-tour-title" className="text-base font-semibold text-[var(--text-color)]">
                {tr(step.titleKey, step.titleFb)}
            </h2>
            <p className="mt-1.5 text-sm leading-snug text-[color-mix(in_srgb,var(--text-color),transparent_20%)]">
                {tr(step.bodyKey, step.bodyFb)}
            </p>

            <div className="mt-4 flex flex-col gap-2">
                {step.primaryAction && onPrimaryAction && (
                    <button
                        type="button"
                        onClick={onPrimaryAction}
                        className="min-h-11 w-full rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_40%)] px-3 py-2 text-sm font-medium text-[var(--text-color)] transition hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_88%)]"
                    >
                        {tr(step.primaryAction.labelKey, step.primaryAction.labelFb)}
                    </button>
                )}
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
                    <button
                        type="button"
                        onClick={onSkip}
                        className="min-h-11 rounded-xl px-3 py-2 text-sm font-medium text-[color-mix(in_srgb,var(--text-color),transparent_30%)] underline-offset-2 hover:underline"
                    >
                        {tr('tour.common.skip', 'Skip tour')}
                    </button>
                    <button
                        type="button"
                        onClick={onNext}
                        className="min-h-11 rounded-xl bg-[var(--accent-color)] px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90"
                    >
                        {isLast ? tr('tour.common.done', 'Done') : tr('tour.common.next', 'Next')}
                    </button>
                </div>
            </div>
        </div>
    );
});

export default TourTooltip;
